// src/pages/Accounting/Reports/AccountList/components/SaveAsModal.tsx

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';

interface SaveAsModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (input: { name: string; shareWith: 'none' | 'everyone' | string; group: string | null }) => void;
  defaultName: string;
}

export default function SaveAsModal({ open, onClose, onSave, defaultName }: SaveAsModalProps) {
  const [name, setName] = useState(defaultName);
  const [shareWith, setShareWith] = useState<'none' | 'everyone' | string>('none');
  const [group, setGroup] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName(defaultName);
      setShareWith('none');
      setGroup(null);
    }
  }, [open, defaultName]);

  if (!open) return null;

  return createPortal(
    <div onClick={onClose} className="fixed inset-0 z-[1000] bg-black/40 flex items-center justify-center p-4">
      <div onClick={(e) => e.stopPropagation()} className="bg-white border border-line rounded-xl shadow-2xl p-6 w-full max-w-[480px]">
        <h4 className="text-[17px] font-extrabold text-ink m-0 mb-1">Save as new report?</h4>
        <p className="text-[12.5px] text-ink3 m-0 mb-4">
          Changes will be saved to the new report. This report will remain unchanged.
        </p>

        <label className="block text-[11.5px] text-ink3 font-semibold mb-1.5">Report name</label>
        <input value={name} onChange={(e) => setName(e.target.value)} className="w-full bg-white border border-line rounded-md px-2.5 py-2 text-[12.5px] text-ink outline-none focus:border-teal-dark mb-3" />

        <label className="block text-[11.5px] text-ink3 font-semibold mb-1.5">Share this report with</label>
        <div className="relative mb-1">
          <select value={shareWith} onChange={(e) => setShareWith(e.target.value as 'none' | 'everyone' | string)} className="w-full bg-white border border-line rounded-md px-2.5 py-2 text-[12.5px] text-ink outline-none focus:border-teal-dark appearance-none cursor-pointer pr-8">
            <option value="none">None</option>
            <option value="everyone">Everyone in your workspace</option>
          </select>
          <ChevronDown size={12} strokeWidth={2.5} className="text-ink3 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
        <div className="text-[11.5px] text-ink4 mb-3">
          {shareWith === 'none' ? 'This report is only visible to you.' : 'Everyone in your workspace can view this report.'}
        </div>

        <label className="block text-[11.5px] text-ink3 font-semibold mb-1.5">Add to group (optional)</label>
        <div className="relative mb-5">
          <select value={group ?? ''} onChange={(e) => setGroup(e.target.value || null)} className="w-full bg-white border border-line rounded-md px-2.5 py-2 text-[12.5px] text-ink outline-none focus:border-teal-dark appearance-none cursor-pointer pr-8">
            <option value="">Select</option>
            <option value="monthly-financials">Monthly financials</option>
            <option value="quarterly-review">Quarterly review</option>
            <option value="year-end">Year-end</option>
          </select>
          <ChevronDown size={12} strokeWidth={2.5} className="text-ink3 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="text-ink3 text-[13px] font-bold px-4 py-2 border-none bg-transparent cursor-pointer hover:text-ink">
            Cancel
          </button>
          <button
            type="button"
            disabled={!name.trim()}
            onClick={() => onSave({ name: name.trim(), shareWith, group })}
            className="bg-teal-dark text-white text-[13px] font-bold px-5 py-2 rounded-md border-none cursor-pointer hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Save
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}